import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Calendar, ArrowLeft, User } from 'lucide-react';
import Navbar from '../Navbar';
import Footer from '../Footer';
import { blogPosts, pageContent, blogContent } from '../../utils/blogData';
import { absoluteUrl } from '../../utils/routeManifest';

const ORGANIZATION = {
  '@type': 'Organization',
  name: 'Etiteks',
  url: absoluteUrl('/'),
  logo: {
    '@type': 'ImageObject',
    url: absoluteUrl('/etiteks-logo.png')
  }
};

const buildArticleSchema = (slug) => {
  const post = blogPosts.en.find((p) => p.id === slug);
  if (!post) return null;
  const published = new Date(post.date);
  if (Number.isNaN(published.getTime())) return null;
  const publishedIso = published.toISOString();
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    image: post.image,
    datePublished: publishedIso,
    dateModified: publishedIso,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': absoluteUrl(`/blog/${post.id}`)
    },
    author: ORGANIZATION,
    publisher: ORGANIZATION
  };
};

const BlogPostPage = () => {
  const { language } = useLanguage();
  const { slug } = useParams();
  const posts = blogPosts[language];
  const t = pageContent[language];
  const post = posts.find((p) => p.id === slug);
  const content = blogContent[language]?.[slug];

  if (!post || !content) {
    return <Navigate to="/blog" replace />;
  }

  const articleSchema = buildArticleSchema(slug);

  return (
    <div className="min-h-screen bg-white" data-testid="blog-post-page">
      {articleSchema && (
        <script type="application/ld+json" data-testid="article-jsonld">
          {JSON.stringify(articleSchema).replace(/</g, '\\u003c')}
        </script>
      )}
      <Navbar forceSolid />

      <article className="pt-32 pb-20 md:pt-40">
        <div className="max-w-3xl mx-auto px-6">
          {/* Back link */}
          <Link
            to="/blog"
            className="inline-flex items-center text-gray-400 hover:text-gray-700 transition-colors mb-10 group text-sm"
            data-testid="blog-back-link"
          >
            <ArrowLeft size={16} className="mr-2 group-hover:-translate-x-1 transition-transform" />
            {language === 'tr' ? 'Blog\'a Don' : 'Back to Blog'}
          </Link>

          {/* Category */}
          <span className="block text-xs font-semibold uppercase tracking-wider text-teal-600 mb-4">
            {post.category}
          </span>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight" data-testid="blog-post-title">
            {post.title}
          </h1>

          {/* Meta */}
          <div className="flex items-center text-sm text-gray-400 space-x-4 mb-10 pb-10 border-b border-gray-200">
            <span className="flex items-center"><User size={14} className="mr-1.5" />{t.author}</span>
            <span className="flex items-center"><Calendar size={14} className="mr-1.5" />{post.date}</span>
            <span>{post.readTime}</span>
          </div>

          {/* Featured Image */}
          <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-12">
            <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          </div>

          {/* Body */}
          <div className="prose prose-lg max-w-none" data-testid="blog-post-body">
            {content.body.map((block, index) => {
              if (typeof block === 'string') {
                return <p key={index} className="text-gray-600 leading-relaxed mb-6">{block}</p>;
              }
              switch (block.type) {
                case 'h2':
                  return <h2 key={index} className="text-2xl font-bold text-gray-900 mt-10 mb-4">{block.text}</h2>;
                case 'h3':
                  return <h3 key={index} className="text-xl font-semibold text-gray-800 mt-8 mb-3">{block.text}</h3>;
                case 'list':
                  return (
                    <ul key={index} className="space-y-2 mb-6 ml-1">
                      {block.items.map((item, i) => (
                        <li key={i} className="flex items-start text-gray-600 leading-relaxed">
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3 mt-2.5 flex-shrink-0"></span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  );
                default:
                  return <p key={index} className="text-gray-600 leading-relaxed mb-6">{block.text}</p>;
              }
            })}
          </div>

          {/* CTA */}
          <div className="mt-16 pt-10 border-t border-gray-200 text-center">
            <p className="text-gray-500 mb-4">
              {language === 'tr'
                ? 'Etiketleme ve ambalaj ihtiyaclariniz hakkinda konusalim.'
                : 'Let\'s talk about your labeling and packaging needs.'}
            </p>
            <Link
              to="/contact"
              className="inline-block bg-gray-800 hover:bg-gray-900 text-white font-medium px-8 py-3 text-sm tracking-wide transition-colors"
              data-testid="blog-post-cta"
            >
              {language === 'tr' ? 'Teklif Alin' : 'Get a Quote'}
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
